import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { useDarkModeCheck } from "../DashboardLayout";
import {
  DOWNLOAD_DOCUMENT_FILE,
  GET_CUSTOMERS,
  GET_REPORT_DATA,
} from "../../../service/graphql/query";
import { logout } from "../../../service/graphql/utils";
import {
  base64ToBlob,
  retrieveFileType,
} from "../../../service/utils/functions";
import * as Storage from "../../../service/storage";
import ReportingPageUI from "../../../ui/dashboard/reporting/ReportingPageUI";
import { CustomerDataEntry } from "../../../service/utils/interfaces";

const FileSaver = require("file-saver");

const ReportingPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkModeCheck();

  const [width, setWidth] = useState(0);
  const [isUserTipping, setIsUserTipping] = useState(false);
  const [readyToFetch, setReadyToFetch] = useState(false);
  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDataEntry>();
  const [yearlyReport, setYearlyReport] = useState<string[][]>([]);
  const [monthlyReport, setMonthlyReport] = useState<string[][]>([]);
  const [currentTab, setCurrentTab] = useState(0);

  const [getCustomers, { data: userData }] = useLazyQuery(GET_CUSTOMERS, {
    errorPolicy: "all",
    onError: () => navigate("/login"),
  });
  const [getReportDocs] = useLazyQuery(GET_REPORT_DATA);
  const [downloadDocFile] = useLazyQuery(DOWNLOAD_DOCUMENT_FILE);

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  const checkUserPermissions = useCallback(
    (customers: any[]) => {
      const user = customers[0];
      if (user) {
        if (user.category !== "Tipping" && !user.pageAuth.reportingPage) {
          window.history.replaceState(null, "", "/");
          navigate("/dashboard/home");
        } else {
          if (user.category === "Tipping") {
            setIsUserTipping(true);
          }
          setReadyToFetch(true);
        }
      } else logout(navigate);
    },
    [navigate]
  );

  useEffect(() => {
    Storage.USER_DATA.get()
      .then(async (customerList) => {
        if (customerList && customerList.length > 0) {
          checkUserPermissions(customerList);
        } else {
          getCustomers({ context: { apiName: "middleware" } });
        }
      })
      .catch(() => {
        getCustomers({ context: { apiName: "middleware" } });
      });
  }, [getCustomers, checkUserPermissions]);

  useEffect(() => {
    if (userData && userData.getCustomers && userData.getCustomers.length > 0) {
      checkUserPermissions(userData.getCustomers);
    }
  }, [userData, checkUserPermissions]);

  useEffect(() => {
    if (!readyToFetch) return;
    if (isUserTipping) {
      const report = getReportDocs({
        variables: { isYtd: true },
        context: { apiName: "middleware" },
      });
      Promise.all([report])
        .then(async (res) => {
          const reportData = res[0].data?.getCustomers;
          if (!reportData) return;
          const tempCustomer = reportData.map((cust: any) => {
            const yearList = cust.reportsTipping.map((rep: any) => [
              rep.periodLabel,
              rep.excelDownloadFilepath,
              rep.PDFDownloadFilepath,
              rep.PDFViewFilepath,
            ]);
            return {
              value: cust.id,
              label: cust.customer,
              yearlyTable: yearList,
            };
          });
          setCustomerList(tempCustomer);
          const filter = await Storage.FILTER_DATA.get();
          const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
          setSelectedCustomer(tempCustomer[idx]);
        })
        .catch((err) => console.error(err));
    } else {
      const yearReport = getReportDocs({
        variables: { isYtd: true },
        context: { apiName: "middleware" },
      });
      const monthReport = getReportDocs({
        variables: { isYtd: false },
        context: { apiName: "middleware" },
      });
      Promise.all([yearReport, monthReport])
        .then(async (combinedRes) => {
          const yearRes = combinedRes[0].data?.getCustomers;
          const monthRes = combinedRes[1].data?.getCustomers;
          if (!yearRes || !monthRes) return;
          const tempCustomer = yearRes.map((yr: any) => {
            const yearList = yr.reports.map((rep: any) => [
              rep.periodLabel,
              rep.excelDownloadFilepath,
              rep.PDFDownloadFilepath,
              rep.PDFViewFilepath,
            ]);
            let monthList = [];
            const monthObj = monthRes.find((mr: any) => mr.id === yr.id);
            if (monthObj) {
              monthList = monthObj.reports.map((rep: any) => [
                rep.periodLabel,
                rep.excelDownloadFilepath,
                rep.PDFDownloadFilepath,
                rep.PDFViewFilepath,
              ]);
            }
            return {
              value: yr.id,
              label: yr.customer,
              yearlyTable: yearList,
              monthlyTable: monthList,
            };
          });
          setCustomerList(tempCustomer);
          const filter = await Storage.FILTER_DATA.get();
          const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
          setSelectedCustomer(tempCustomer[idx]);
        })
        .catch((err) => console.error(err));
    }

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [readyToFetch, isUserTipping, getReportDocs]);

  useEffect(() => {
    if (selectedCustomer) {
      setYearlyReport(selectedCustomer?.yearlyTable ?? []);
      setMonthlyReport(selectedCustomer?.monthlyTable ?? []);
    }
  }, [selectedCustomer]);

  const manageFile = async (link: string, download: boolean) => {
    let downloadLink = link;
    const fileName = link.split("\\").pop();
    const fileExtension = fileName?.split(".")[1];
    if (!fileName || !fileExtension) return;
    const docFileRes = await downloadDocFile({
      variables: {
        fileId: downloadLink,
        source: "local",
      },
      context: { apiName: "middleware" },
    });
    if (!docFileRes.data?.downloadProtectedDocument) return;
    let fileType = retrieveFileType(fileExtension);
    const blob = base64ToBlob(
      docFileRes.data.downloadProtectedDocument,
      fileType
    );
    if (download) FileSaver.saveAs(blob, fileName);
    else {
      const file = new File([blob], `${fileName}.${fileExtension}`, {
        type: fileType,
      });
      const url = URL.createObjectURL(file);
      const win = window.open(url, "_blank");
      if (win) win.focus();
    }
  };

  return (
    <ReportingPageUI
      customerList={customerList}
      currentTab={currentTab}
      isUserTipping={isUserTipping}
      yearlyReport={yearlyReport}
      monthlyReport={monthlyReport}
      setSelectedCustomer={setSelectedCustomer}
      setCurrentTab={setCurrentTab}
      manageFile={manageFile}
      darkMode={darkMode}
      width={width}
    />
  );
};

export default ReportingPage;
